import { type ReactNode, useEffect, useState } from 'react';
import { canWriteStaffScope, isRestrictedStaff } from '../lib/staff-access.js';
import { Link, useParams } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { useSettings } from '../lib/settings-context.js';
import { useAuth } from '../lib/auth-context.js';
import { UserPasswordResetButton } from '../components/UserPasswordResetButton.js';
import { PaidUntilChip } from '../components/PaidUntilChip.js';
import { formatCents, formatDate } from '../lib/format.js';
import { RecordPaymentDialog } from '../components/RecordPaymentDialog.js';
import { ReceivePalletDialog } from '../components/ReceivePalletDialog.js';
import { PushInvoiceDialog } from '../components/PushInvoiceDialog.js';
import { BillingBatchesTable } from '../components/BillingBatchesTable.js';
import { BillingAccountCard } from '../components/BillingAccountCard.js';
import { EditCustomerDialog } from '../components/EditCustomerDialog.js';
import { GrantPartnerRoleDialog } from '../components/GrantPartnerRoleDialog.js';

const { t } = i18n;

interface PalletStats {
  currentCounts: Record<string, number>;
  currentHeld: number;
  period: { from: string; to: string };
  arrivedInPeriod: number;
  dispatchedInPeriod: number;
  netInPeriod: number;
}

interface CustomerUserRow {
  userId: string;
  email: string;
  displayName: string;
  status: 'ACTIVE' | 'INACTIVE';
  roles: {
    id: string;
    role: string;
    scopeType: string;
    scopeId: string | null;
    partner: { id: string; code: string; name: string } | null;
  }[];
}

interface CustomerDetail {
  id: string;
  code: string;
  name: string;
  status: 'ACTIVE' | 'ARCHIVED';
  billingMode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY';
  contactPerson: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  billingAddress: string | null;
  vatId: string | null;
  pricingTierId: string | null;
  pricingTier: { id: string; name: string } | null;
  customRateCents: number | null;
  notes: string | null;
  paidUntil: string | null;
  lastPaymentAt: string | null;
  payments: {
    id: string;
    amountCents: number;
    currency: string;
    paidAt: string;
    periodFrom: string | null;
    periodTo: string | null;
    reference: string | null;
  }[];
  pallets: {
    id: string;
    code: string;
    state: string;
    expectedAt: string | null;
    arrivedAt: string | null;
    dispatchedAt: string | null;
    comment: string | null;
  }[];
}

export function CustomerDetailPage() {
  const { id = '' } = useParams();
  const settings = useSettings();
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [palletOpen, setPalletOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [users, setUsers] = useState<CustomerUserRow[] | null>(null);
  const { user: currentUser } = useAuth();
  const isAdmin =
    (currentUser?.roles ?? []).some((r) => r.role === 'ADMIN') &&
    canWriteStaffScope(currentUser, 'STORAGE_CUSTOMER', id);
  const [grantFor, setGrantFor] = useState<{ userId: string; label: string } | null>(null);
  const [stats, setStats] = useState<PalletStats | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [statsFrom, setStatsFrom] = useState(firstOfMonth);
  const [statsTo, setStatsTo] = useState(today);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  async function onToggleArchive() {
    if (!customer) return;
    const archiving = customer.status === 'ACTIVE';
    if (archiving && !confirm(t('customer.archive.confirm'))) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const action = archiving ? 'archive' : 'unarchive';
      await api(`/customers/${customer.id}/${action}`, { method: 'POST' });
      setReloadKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'customer_has_active_pallets') {
        setArchiveError(t('customer.archive.has_active_pallets'));
      } else {
        setArchiveError(t('common.error'));
      }
    } finally {
      setArchiveBusy(false);
    }
  }

  useEffect(() => {
    setCustomer(null);
    api<CustomerDetail>(`/customers/${id}`)
      .then(setCustomer)
      .catch((err: unknown) => setError(String(err)));
  }, [id, reloadKey]);

  useEffect(() => {
    setUsers(null);
    api<CustomerUserRow[]>(`/customers/${id}/users`)
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [id, reloadKey]);

  useEffect(() => {
    setStats(null);
    const qs = new URLSearchParams();
    if (statsFrom) qs.set('from', statsFrom);
    if (statsTo) qs.set('to', statsTo);
    api<PalletStats>(`/customers/${id}/pallet-stats?${qs.toString()}`)
      .then(setStats)
      .catch(() => setStats(null));
  }, [id, statsFrom, statsTo, reloadKey]);

  async function revokeRole(userId: string, userRoleId: string) {
    if (!confirm(t('customer.grant_partner.revoke_confirm'))) return;
    try {
      await api(`/customers/${id}/users/${userId}/roles/${userRoleId}`, {
        method: 'DELETE',
      });
      setReloadKey((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    }
  }

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!customer) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/customers" className="text-xs text-muted-foreground hover:underline">
            ← {t('customer.list.title')}
          </Link>
          <h2 className="text-xl font-semibold mt-1">
            <span className="font-mono text-base text-muted-foreground mr-2">{customer.code}</span>
            {customer.name}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <PaidUntilChip
            paidUntil={customer.paidUntil}
            thresholdDays={settings.paidUntilWarningDays}
          />
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            disabled={!canWriteStaffScope(currentUser, 'STORAGE_CUSTOMER', id)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('common.edit')}
          </button>
          <button
            type="button"
            onClick={() => void onToggleArchive()}
            disabled={archiveBusy || !canWriteStaffScope(currentUser, 'STORAGE_CUSTOMER', id)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
          >
            {customer.status === 'ACTIVE'
              ? t('customer.archive.action')
              : t('customer.unarchive.action')}
          </button>
          <button
            type="button"
            onClick={() => setBillingOpen(true)}
            disabled={!canWriteStaffScope(currentUser, 'STORAGE_CUSTOMER', id)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('customer.action.push_invoice')}
          </button>
          <button
            type="button"
            onClick={() => setPaymentOpen(true)}
            disabled={!canWriteStaffScope(currentUser, 'STORAGE_CUSTOMER', id)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('customer.action.record_payment')}
          </button>
          <button
            type="button"
            onClick={() => setPalletOpen(true)}
            disabled={!canWriteStaffScope(currentUser, 'STORAGE_CUSTOMER', id)}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm"
          >
            {t('customer.action.receive_pallet')}
          </button>
        </div>
      </div>

      {customer.status === 'ARCHIVED' ? (
        <div className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          {t('customer.archive.banner')}
        </div>
      ) : null}
      {archiveError ? (
        <div className="rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
          {archiveError}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 text-sm">
        <Info label={t('customer.col.billing_mode')}>
          {t(`customer.billing.${customer.billingMode}`)}
        </Info>
        {customer.customRateCents != null ? (
          <Info label={t('billing.custom_rate.label')}>
            {formatCents(customer.customRateCents)}
            {customer.billingMode === 'FIXED_MONTHLY'
              ? ` / ${t('pricing.per_month')}`
              : ` / ${t('pricing.per_day')}`}
          </Info>
        ) : null}
        <Info label={t('customer.col.pricing_tier')}>
          {customer.pricingTier?.name ?? t('customer.pricing_tier.none')}
        </Info>
        <Info label={t('customer.col.last_payment')}>{formatDate(customer.lastPaymentAt)}</Info>
        <Info label={t('customer.contact.person')}>{customer.contactPerson ?? '—'}</Info>
        <Info label={t('customer.contact.email')}>{customer.contactEmail ?? '—'}</Info>
        <Info label={t('customer.contact.phone')}>{customer.contactPhone ?? '—'}</Info>
        <Info label={t('customer.col.billing_address')}>
          {customer.billingAddress ? (
            <span className="whitespace-pre-line">{customer.billingAddress}</span>
          ) : (
            '—'
          )}
        </Info>
        <Info label={t('customer.col.vat_id')}>{customer.vatId ?? '—'}</Info>
        {customer.notes ? (
          <Info label={t('customer.col.notes')}>
            <span className="whitespace-pre-line">{customer.notes}</span>
          </Info>
        ) : null}
      </div>

      <div className="rounded border border-border p-3 text-sm space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('pallet.stats.title')}
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1">
              <span className="text-muted-foreground">{t('pallet.stats.from')}</span>
              <input
                type="date"
                value={statsFrom}
                onChange={(e) => setStatsFrom(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1"
              />
            </label>
            <label className="flex items-center gap-1">
              <span className="text-muted-foreground">{t('pallet.stats.to')}</span>
              <input
                type="date"
                value={statsTo}
                onChange={(e) => setStatsTo(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1"
              />
            </label>
          </div>
        </div>
        {stats ? (
          <div className="grid gap-3 md:grid-cols-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('pallet.stats.now')}
              </div>
              <div className="text-2xl font-semibold tabular-nums">{stats.currentHeld}</div>
              <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                {Object.entries(stats.currentCounts).map(([state, count]) => (
                  <span
                    key={state}
                    className="rounded-full border border-border bg-muted px-2 py-0.5"
                  >
                    {t(`pallet.state.${state}`)}: {count}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('pallet.stats.arrived_in_period')}
              </div>
              <div className="text-2xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                +{stats.arrivedInPeriod}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('pallet.stats.dispatched_in_period')}
              </div>
              <div className="text-2xl font-semibold tabular-nums text-sky-700 dark:text-sky-300">
                −{stats.dispatchedInPeriod}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('pallet.stats.net_in_period')}
              </div>
              <div
                className={`text-2xl font-semibold tabular-nums ${
                  stats.netInPeriod > 0
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : stats.netInPeriod < 0
                      ? 'text-sky-700 dark:text-sky-300'
                      : ''
                }`}
              >
                {stats.netInPeriod > 0 ? '+' : ''}
                {stats.netInPeriod}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">{t('common.loading')}</div>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('pallet.list.title')}
        </h3>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('pallet.col.code')}</th>
                <th className="px-3 py-2 font-medium">{t('pallet.col.state')}</th>
                <th className="px-3 py-2 font-medium">{t('pallet.col.arrived')}</th>
                <th className="px-3 py-2 font-medium">{t('pallet.col.dispatched')}</th>
                <th className="px-3 py-2 font-medium">{t('pallet.col.comment')}</th>
              </tr>
            </thead>
            <tbody>
              {customer.pallets.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    {t('pallet.list.empty')}
                  </td>
                </tr>
              ) : (
                customer.pallets.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/pallets/${p.id}`} className="hover:underline">
                        {p.code}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-xs">{t(`pallet.state.${p.state}`)}</td>
                    <td className="px-3 py-2">{formatDate(p.arrivedAt)}</td>
                    <td className="px-3 py-2">{formatDate(p.dispatchedAt)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-xs">
                      {p.comment ?? ''}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('customer.users.title')}
        </h3>
        {users === null ? (
          <div className="text-xs text-muted-foreground">{t('common.loading')}</div>
        ) : users.length === 0 ? (
          <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
            {t('customer.users.empty')}
          </div>
        ) : (
          <ul className="space-y-2">
            {users.map((u) => {
              const partnerRoles = u.roles.filter((r) => r.scopeType === 'PARTNER' && r.partner);
              return (
                <li key={u.userId} className="rounded border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{u.displayName}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isAdmin && !isRestrictedStaff(currentUser) ? (
                        <UserPasswordResetButton userId={u.userId} email={u.email} />
                      ) : null}
                      <button
                        type="button"
                        onClick={() =>
                          setGrantFor({ userId: u.userId, label: `${u.displayName} (${u.email})` })
                        }
                        className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                      >
                        {t('customer.grant_partner.action')}
                      </button>
                    </div>
                  </div>
                  {partnerRoles.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {partnerRoles.map((r, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-100 px-2 py-0.5 text-xs text-violet-900 dark:border-violet-700 dark:bg-violet-900/40 dark:text-violet-100"
                        >
                          <span className="font-mono">{r.partner!.code}</span>
                          <span className="opacity-70">·</span>
                          <span>{t(`role.${r.role}`)}</span>
                          <button
                            type="button"
                            onClick={() => void revokeRole(u.userId, r.id)}
                            className="ml-1 text-[10px] hover:underline"
                            title={t('common.delete')}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <BillingAccountCard owner="customers" ownerId={customer.id} reloadKey={reloadKey} />

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('payment.list.title')}
        </h3>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('payment.col.paid_at')}</th>
                <th className="px-3 py-2 font-medium">{t('payment.col.amount')}</th>
                <th className="px-3 py-2 font-medium">{t('payment.col.period')}</th>
                <th className="px-3 py-2 font-medium">{t('payment.col.reference')}</th>
              </tr>
            </thead>
            <tbody>
              {customer.payments.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                    {t('payment.list.empty')}
                  </td>
                </tr>
              ) : (
                customer.payments.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-3 py-2">{formatDate(p.paidAt)}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {formatCents(p.amountCents, p.currency)}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {p.periodFrom && p.periodTo
                        ? `${formatDate(p.periodFrom)} → ${formatDate(p.periodTo)}`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">{p.reference ?? ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BillingBatchesTable customerId={customer.id} reloadKey={reloadKey} />

      {paymentOpen ? (
        <RecordPaymentDialog
          customerId={customer.id}
          onClose={() => setPaymentOpen(false)}
          onRecorded={() => {
            setPaymentOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {palletOpen ? (
        <ReceivePalletDialog
          customerId={customer.id}
          onClose={() => setPalletOpen(false)}
          onCreated={() => {
            setPalletOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {billingOpen ? (
        <PushInvoiceDialog
          customerId={customer.id}
          onClose={() => setBillingOpen(false)}
          onPushed={() => setReloadKey((k) => k + 1)}
        />
      ) : null}
      {editOpen ? (
        <EditCustomerDialog
          customer={customer}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {grantFor ? (
        <GrantPartnerRoleDialog
          customerId={customer.id}
          userId={grantFor.userId}
          userLabel={grantFor.label}
          onClose={() => setGrantFor(null)}
          onGranted={() => {
            setGrantFor(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </section>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}
